/**
 * Photos (deck rulings c043, c057, k11; docs/52 S16 and the library under Jobs): add photos from a phone and manage
 * the library.
 *
 * `PhotoAdder` is the one upload surface, used by the library and by the get live step. The client says what the
 * photos show, then takes or picks several. Each is resized on the phone, uploads at once with its own progress
 * ring, and can be retried on its own. With no signal the photos are kept on the phone (the outbox) and go up when
 * the app is back online, even if it was closed meanwhile (docs/54 M4).
 *
 * The library shows every photo they sent with its state in plain words (checking, with us for a look, ready, on
 * the site, or why it can't be used), and lets them name it, mark a favorite, say what it shows, set it aside or
 * remove it. Before go-live a card says exactly what the site still needs.
 */
import { Banner, Button, Chip, EmptyState, Filters, Icon, SelectField, Sheet, TextField, type Tone } from '@ff/app-ui';
import { PHOTO_KINDS, jobs as J, type PhotoKind, type PhotoNeeds } from '@ff/contracts';
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { z } from 'zod';
import { ApiError, errorText, idemKey } from '../lib/api.ts';
import { invalidate, online, send, setCached, useOnline, useQuery } from '../lib/data.ts';
import { enqueue, outbox } from '../lib/outbox.ts';
import { location, scoped } from '../lib/router.ts';
import { useStore } from '../lib/store.ts';
import { haptic, toast } from '../lib/ui.ts';
import { processPhoto, uploadBlob, type Processed } from '../lib/upload.ts';
import { S } from '@ff/app-ui/copy';
import { Loaded, Screen } from '../ui/common.tsx';

const { UPLOAD_CONTENT_TYPES, UPLOAD_LIMITS } = J;
type Media = z.infer<typeof J.MediaView>;
const PHOTO_TYPES = UPLOAD_CONTENT_TYPES.photo as readonly string[];

/** "Your site still needs 2 more photos of finished work and 1 photo of your van or shop." */
export function needsLine(needs: PhotoNeeds): string {
  if (needs.met) return S.photos.allApproved;
  if (!needs.missing.length) return S.photos.allSent;
  return S.photos.stillNeeded(needs.missing.map((m) => S.photos.need[m.need](m.count)));
}

export function PhotoNeedsCard({ needs }: { needs: PhotoNeeds }): JSX.Element {
  const total = needs.rule.work + needs.rule.people + needs.rule.place;
  const sent = Math.min(needs.sent.work, needs.rule.work) + Math.min(needs.sent.people, needs.rule.people) + Math.min(needs.sent.place, needs.rule.place);
  return (
    <Banner tone={needs.met ? 'ok' : needs.missing.length ? 'warn' : 'info'} icon={needs.met ? 'check' : 'image'} title={needsLine(needs)}>
      {!needs.met && <p class="t-small">{S.photos.progress(sent, total)}</p>}
    </Banner>
  );
}

// ------------------------------------------------------------------------------------------ adding photos

interface Pick {
  key: string;
  name: string;
  url: string;
  state: 'processing' | 'uploading' | 'done' | 'failed' | 'waiting';
  progress: number;
  processed: Processed | null;
  error: string | null;
}

/** Kinds a client picks from when adding photos. A logo is offered last: it's a brand file, not a site photo. */
const KIND_OPTIONS = PHOTO_KINDS.map((k) => ({ value: k, label: S.photos.kind[k] ?? k }));

export function PhotoAdder({ onSent, services }: { onSent?: () => void; services?: { id: string; name: string }[] }): JSX.Element {
  const isOnline = useOnline();
  const [kind, setKind] = useState<PhotoKind | ''>('');
  const [serviceId, setServiceId] = useState('');
  const [picks, setPicks] = useState<Pick[]>([]);
  const [error, setError] = useState<string | null>(null);
  const picksRef = useRef<Pick[]>([]);
  picksRef.current = picks;
  const hint = useRef<{ kind?: PhotoKind; serviceId?: string }>({});
  hint.current = { ...(kind ? { kind } : {}), ...(serviceId && (kind === 'work' || kind === 'before' || kind === 'after') ? { serviceId } : {}) };
  const camRef = useRef<HTMLInputElement>(null);
  const libRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => picksRef.current.forEach((p) => URL.revokeObjectURL(p.url)), []);
  const update = (k: string, patch: Partial<Pick>) => setPicks((ps) => ps.map((p) => (p.key === k ? { ...p, ...patch } : p)));

  /** No signal: the photo is kept on the phone with what it shows, and goes up when the app is back online. */
  const park = async (p: Pick & { processed: Processed }) => {
    await enqueue({ id: p.key, kind: 'photos', body: { ...hint.current }, photos: [{ key: p.key, contentType: p.processed.contentType, uploadId: null }] }, { [p.key]: p.processed.blob });
    update(p.key, { state: 'waiting', error: null });
  };

  const upload = async (p: Pick) => {
    if (!p.processed) return;
    const ready = p as Pick & { processed: Processed };
    if (!online.get()) return park(ready);
    update(p.key, { state: 'uploading', progress: 0.05, error: null });
    try {
      await uploadBlob(p.processed.blob, p.processed.contentType, 'photo', 'job', (f) => update(p.key, { progress: f }), undefined, hint.current);
      update(p.key, { state: 'done', progress: 1 });
      haptic();
      invalidate('media', 'onboarding', 'home');
      onSent?.();
    } catch (e) {
      if (e instanceof ApiError && e.offline) await park(ready);
      else update(p.key, { state: 'failed', error: errorText(e, S.photos.uploadFailed) });
    }
  };

  const add = (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    for (const f of Array.from(files).slice(0, UPLOAD_LIMITS.filesPerJob)) {
      if (!f.type.startsWith('image/') && !PHOTO_TYPES.includes(f.type) && !/\.(heic|heif)$/i.test(f.name)) {
        setError(S.jobs.notPhoto(f.name));
        continue;
      }
      if (f.size > UPLOAD_LIMITS.photoBytes) {
        setError(S.jobs.tooBig(f.name));
        continue;
      }
      const p: Pick = { key: idemKey(), name: f.name, url: URL.createObjectURL(f), state: 'processing', progress: 0, processed: null, error: null };
      setPicks((ps) => [...ps, p]);
      void processPhoto(f).then((processed) => {
        update(p.key, { processed });
        void upload({ ...p, processed });
      });
    }
  };

  const done = picks.filter((p) => p.state === 'done').length;
  const waiting = picks.filter((p) => p.state === 'waiting').length;
  const failed = picks.filter((p) => p.state === 'failed');
  const pick = (ref: { current: HTMLInputElement | null }) => {
    if (!kind) {
      setError(S.photos.kindUnset);
      return;
    }
    ref.current?.click();
  };

  return (
    <section class="stack" data-gap="m" aria-label={S.photos.add}>
      <SelectField label={S.photos.kindLabel} hint={S.photos.kindHint} value={kind} onChange={(v) => (setKind(v), setError(null))} placeholder={S.jobs.pick} options={KIND_OPTIONS} error={error === S.photos.kindUnset ? error : null} />
      {(kind === 'work' || kind === 'before' || kind === 'after') && services && services.length > 0 && (
        <SelectField label={S.photos.serviceLabel} value={serviceId} onChange={setServiceId} placeholder={S.photos.serviceNone} options={services.map((s) => ({ value: s.id, label: s.name }))} />
      )}
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => (add((e.currentTarget as HTMLInputElement).files), ((e.currentTarget as HTMLInputElement).value = ''))} />
      <input ref={libRef} type="file" accept="image/*,.heic,.heif" multiple hidden data-testid="library-photo-input" onChange={(e) => (add((e.currentTarget as HTMLInputElement).files), ((e.currentTarget as HTMLInputElement).value = ''))} />
      <div class="btn-row" data-equal>
        <Button variant="primary" icon="camera" onClick={() => pick(camRef)}>{S.jobs.takePhoto}</Button>
        <Button icon="image" onClick={() => pick(libRef)}>{S.jobs.choosePhotos}</Button>
      </div>
      <p class="t-small t-muted">
        <Icon name="shield" size={16} /> {S.jobs.privacy}
      </p>
      {error && error !== S.photos.kindUnset && <Banner tone="danger" role="alert" title={error} />}
      {picks.length > 0 && (
        <>
          <p class="t-small" role="status" aria-live="polite">{done === picks.length ? S.photos.sent(done) : S.photos.uploading(done, picks.length)}</p>
          <ul role="list" class="thumbs">
            {picks.map((p, i) => (
              <li key={p.key} class="thumb-wrap">
                <div class="thumb" data-state={p.state}>
                  <img src={p.url} alt={S.jobs.photoN(i + 1, picks.length)} />
                  <span class="thumb-state" aria-hidden="true">
                    {p.state === 'done' ? <Icon name="check" /> : p.state === 'failed' ? <Icon name="refresh" /> : p.state === 'waiting' ? <Icon name="offline" /> : <Ring f={p.progress} />}
                  </span>
                </div>
                {p.state === 'failed' && (
                  <button type="button" class="icon-btn" onClick={() => void upload(p)} aria-label={`${S.jobs.retry}, ${S.jobs.photoN(i + 1, picks.length)}`}>
                    <Icon name="refresh" size={18} /> <span>{S.jobs.retry}</span>
                  </button>
                )}
                {p.error && <p class="field-error">{p.error}</p>}
              </li>
            ))}
          </ul>
          {failed.length > 1 && <Button onClick={() => failed.forEach((p) => void upload(p))} icon="refresh">{S.photos.retryAll}</Button>}
        </>
      )}
      {(waiting > 0 || !isOnline) && <Banner tone="warn" icon="offline" title={S.photos.waitingOffline} />}
    </section>
  );
}

function Ring({ f }: { f: number }): JSX.Element {
  const r = 10;
  const c = 2 * Math.PI * r;
  return (
    <svg class="up-ring" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r={r} fill="none" stroke="currentColor" stroke-opacity="0.3" stroke-width="3" />
      <circle cx="12" cy="12" r={r} fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray={c} stroke-dashoffset={c * (1 - Math.max(0.05, f))} />
    </svg>
  );
}

// ------------------------------------------------------------------------------------------ the library

type Filter = 'all' | 'favourites' | 'used' | 'flagged';

/** A photo's state in the client's words, and the tone of its chip. */
export function photoState(m: Media): { label: string; tone: Tone; note: string | null } {
  if (m.processing) return { label: S.photos.state.checking!, tone: 'neutral', note: S.photos.checkingNote };
  if (m.screenState === 'rejected') return { label: S.photos.state.cantUse!, tone: 'danger', note: m.screenNote };
  if (m.excluded) return { label: S.photos.state.setAside!, tone: 'muted', note: null };
  if (m.screenState === 'ok') return (m.placedIn?.length ?? 0) > 0 || m.usedIn.length > 0 ? { label: S.photos.state.onSite!, tone: 'ok', note: null } : { label: S.photos.state.approved!, tone: 'ok', note: null };
  return { label: S.photos.state.waiting!, tone: 'new', note: m.screenState === 'flagged' ? m.screenNote : S.photos.waitingNote };
}

export function PhotoLibraryScreen(): JSX.Element {
  const loc = useStore(location);
  const queued = useStore(outbox).filter((i) => i.kind === 'photos').reduce((n, i) => n + i.photos.length, 0);
  const [filter, setFilter] = useState<Filter>('all');
  const [cursors, setCursors] = useState<string[]>([]);
  const q = useQuery('media', { query: { ...(loc.scope ? { location: loc.scope } : {}), ...(filter !== 'all' ? { filter } : {}) } });
  const opts = useQuery('jobOptions', { query: loc.scope ? { location: loc.scope } : {} });
  const [more, setMore] = useState<Media[]>([]);
  const [next, setNext] = useState<string | null | undefined>(undefined);
  const [open, setOpen] = useState<Media | null>(null);
  const [adding, setAdding] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // A new filter, or fresh first page, starts the list over.
  useEffect(() => {
    setMore([]);
    setNext(undefined);
    setCursors([]);
  }, [filter, q.updatedAt]);
  // Photos being checked finish within a minute or so: look again while any is.
  const anyChecking = !!q.data?.items.some((m) => m.processing);
  useEffect(() => {
    if (!anyChecking) return;
    const t = setInterval(() => void q.refetch(), 8000);
    return () => clearInterval(t);
  }, [anyChecking]);

  const items = [...(q.data?.items ?? []), ...more];
  const cursor = next === undefined ? (q.data?.nextCursor ?? null) : next;
  const loadMore = async () => {
    if (!cursor || cursors.includes(cursor)) return;
    setLoadingMore(true);
    try {
      const { call } = await import('../lib/api.ts');
      const page = await call('media', { query: { ...(loc.scope ? { location: loc.scope } : {}), ...(filter !== 'all' ? { filter } : {}), cursor } } as never);
      setCursors((c) => [...c, cursor]);
      setMore((m) => [...m, ...page.items]);
      setNext(page.nextCursor);
    } catch (e) {
      toast(errorText(e, S.errors.offlineSend));
    } finally {
      setLoadingMore(false);
    }
  };
  const changed = (m: Media) => {
    setCached('media', { query: { ...(loc.scope ? { location: loc.scope } : {}), ...(filter !== 'all' ? { filter } : {}) } }, (prev) => (prev ? { ...prev, items: prev.items.map((x) => (x.id === m.id ? m : x)) } : prev));
    setMore((ms) => ms.map((x) => (x.id === m.id ? m : x)));
    setOpen((o) => (o && o.id === m.id ? m : o));
  };
  const removed = (id: string) => {
    setCached('media', { query: { ...(loc.scope ? { location: loc.scope } : {}), ...(filter !== 'all' ? { filter } : {}) } }, (prev) => (prev ? { ...prev, items: prev.items.filter((x) => x.id !== id) } : prev));
    setMore((ms) => ms.filter((x) => x.id !== id));
    setOpen(null);
    invalidate('media', 'onboarding', 'home', 'jobs');
  };

  return (
    <Screen title={S.photos.title} back={scoped('/jobs', loc.scope)} lead={S.photos.lead} query={q}>
      <div class="stack" data-gap="l">
        {q.data?.needs && <PhotoNeedsCard needs={q.data.needs} />}
        {queued > 0 && <Banner tone="warn" icon="offline" title={S.app.offlineQueued(queued)} />}
        {adding ? <PhotoAdder services={opts.data?.services} onSent={() => void q.refetch()} /> : <Button variant="primary" size="lg" icon="camera" onClick={() => setAdding(true)}>{S.photos.add}</Button>}
        <Filters label={S.photos.title} value={filter} onChange={setFilter} options={(['all', 'favourites', 'used', 'flagged'] as Filter[]).map((f) => ({ value: f, label: S.photos.filter[f]! }))} />
        <Loaded q={q} skeleton="cards">
          {() =>
            items.length ? (
              <>
                <ul role="list" class="library">
                  {items.map((m, i) => {
                    const st = photoState(m);
                    const name = S.photos.photoOf(i + 1, items.length, m.title ?? '');
                    return (
                      <li key={m.id} data-excluded={m.excluded || m.screenState === 'rejected' ? '' : undefined}>
                        <button type="button" class="library-photo" onClick={() => setOpen(m)} aria-label={`${name}. ${st.label}`}>
                          {m.processing ? <span class="library-pending" aria-hidden="true"><Icon name="image" size={28} /></span> : <img src={m.thumbUrl} alt="" loading="lazy" decoding="async" width={m.width ?? undefined} height={m.height ?? undefined} />}
                          {m.favourite && <span class="library-fav" aria-hidden="true"><Icon name="star" size={16} /></span>}
                        </button>
                        <Chip tone={st.tone}>{st.label}</Chip>
                        <span class="t-small">{m.title ?? (m.kind ? S.photos.kind[m.kind] : S.photos.kindUnset)}</span>
                      </li>
                    );
                  })}
                </ul>
                {cursor && <Button onClick={() => void loadMore()} busy={loadingMore}>{S.photos.more}</Button>}
              </>
            ) : (
              <EmptyState title={S.photos.empty} body={S.photos.emptyLead} />
            )
          }
        </Loaded>
      </div>
      {open && <PhotoSheet m={open} services={opts.data?.services ?? []} onClose={() => setOpen(null)} onChange={changed} onRemoved={removed} />}
    </Screen>
  );
}

function PhotoSheet({ m, services, onClose, onChange, onRemoved }: { m: Media; services: { id: string; name: string }[]; onClose: () => void; onChange: (m: Media) => void; onRemoved: (id: string) => void }): JSX.Element {
  const [title, setTitle] = useState(m.title ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const st = photoState(m);
  const places = [...(m.placedIn ?? [])];

  const patch = async (what: string, body: z.input<typeof J.MediaUpdateBody>) => {
    setBusy(what);
    setError(null);
    try {
      onChange(await send('mediaUpdate', { params: { asset: m.id }, body }));
      invalidate('onboarding', 'home');
    } catch (e) {
      setError(errorText(e, S.errors.offlineSend));
    } finally {
      setBusy(null);
    }
  };
  const remove = async () => {
    setBusy('remove');
    setError(null);
    try {
      const r = await send('mediaRemove', { params: { asset: m.id } });
      toast(r.wasOnSite ? `${S.photos.removed}. ${S.photos.removeOnSite}` : S.photos.removed);
      onRemoved(m.id);
    } catch (e) {
      setError(errorText(e, S.errors.offlineSend));
      setBusy(null);
    }
  };

  return (
    <Sheet open onClose={onClose} title={m.title ?? (m.kind ? S.photos.kind[m.kind] : S.photos.title)} closeLabel={S.app.close} description={st.label}>
      <div class="stack" data-gap="m">
        {!m.processing && <img class="sheet-photo" src={m.fullUrl} alt={m.title ?? (m.kind ? S.photos.kind[m.kind]! : S.photos.title)} width={m.width ?? undefined} height={m.height ?? undefined} />}
        <div class="cluster">
          <Chip tone={st.tone}>{st.label}</Chip>
          {m.favourite && <Chip tone="outline" icon="star">{S.photos.isFavourite}</Chip>}
        </div>
        {st.note && <p role={m.screenState === 'rejected' ? 'alert' : undefined}>{st.note}</p>}
        {m.screenState === 'ok' && !m.excluded && <p class="t-small t-muted">{places.length ? S.photos.placedIn(places) : S.photos.notPlaced}</p>}
        {error && <Banner tone="danger" role="alert" title={error} />}
        {m.canEdit && !confirm && (
          <>
            <form
              class="stack"
              data-gap="s"
              onSubmit={(e) => {
                e.preventDefault();
                if (title.trim() !== (m.title ?? '')) void patch('title', { title: title.trim() });
              }}
            >
              <TextField label={S.photos.nameLabel} hint={S.photos.nameHint} value={title} onInput={setTitle} maxLength={80} />
              <Button type="submit" busy={busy === 'title'} disabled={title.trim() === (m.title ?? '')}>{S.photos.save}</Button>
            </form>
            {!m.processing && m.screenState !== 'rejected' && (
              <>
                <SelectField label={S.photos.kindChange} value={m.kind ?? ''} onChange={(v) => v && v !== m.kind && void patch('kind', { kind: v })} placeholder={S.photos.kindUnset} options={KIND_OPTIONS} />
                {(m.kind === 'work' || m.kind === 'before' || m.kind === 'after') && services.length > 0 && (
                  <SelectField label={S.photos.serviceLabel} value={m.serviceId ?? ''} onChange={(v) => void patch('service', { serviceId: (v || null) as never })} placeholder={S.photos.serviceNone} options={services.map((s) => ({ value: s.id, label: s.name }))} />
                )}
                <div class="btn-row">
                  <Button icon="star" busy={busy === 'fav'} onClick={() => void patch('fav', { favourite: !m.favourite })}>{m.favourite ? S.photos.unfavourite : S.photos.favourite}</Button>
                  <Button variant="quiet" busy={busy === 'aside'} onClick={() => void patch('aside', { excluded: !m.excluded })}>{m.excluded ? S.photos.useAgain : S.photos.dontUse}</Button>
                </div>
              </>
            )}
            <Button variant="danger" icon="trash" onClick={() => setConfirm(true)}>{S.photos.remove}</Button>
          </>
        )}
        {m.canEdit && confirm && (
          <div class="stack" data-gap="s" role="group" aria-label={S.photos.removeTitle}>
            <p class="t-heading">{S.photos.removeTitle}</p>
            <p>{S.photos.removeLead}{places.length ? ` ${S.photos.removeOnSite}` : ''}</p>
            <div class="btn-row">
              <Button variant="danger-solid" busy={busy === 'remove'} onClick={() => void remove()}>{S.photos.removeConfirm}</Button>
              <Button onClick={() => setConfirm(false)}>{S.photos.keep}</Button>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}
